import { X } from "lucide-react";
import {
  composeRenderProps,
  OverlayArrow,
  Tooltip as RACTooltip,
  type TooltipProps as RACTooltipProps,
  TooltipTrigger as RACTooltipTrigger,
  type TooltipTriggerComponentProps
} from "react-aria-components";
import { cn, tv } from "tailwind-variants";

const tooltipStyles = tv({
  base: "bg-primary text-primary-foreground z-50 flex w-fit max-w-[96vw] items-center gap-2 text-balance rounded-md px-3 py-1.5 text-xs",
  variants: {
    isEntering: {
      true: "animate-in fade-in-0 zoom-in-95 data-[placement=bottom]:slide-in-from-top-2 data-[placement=left]:slide-in-from-right-2 data-[placement=right]:slide-in-from-left-2 data-[placement=top]:slide-in-from-bottom-2 duration-200"
    },
    isExiting: {
      true: "animate-out fade-out-0 zoom-out-95 duration-150"
    }
  }
});

export type TooltipTriggerProps = TooltipTriggerComponentProps;

export function TooltipTrigger(props: TooltipTriggerProps) {
  return <RACTooltipTrigger {...props} />;
}

export type TooltipProps = Omit<RACTooltipProps, "children"> & {
  children: React.ReactNode;
  showArrow?: boolean;
  arrowClassName?: string;
  onClose?: ((event: React.MouseEvent<Element>) => void) | undefined;
  crossOffset?: number;
};

export function Tooltip({
  children,
  className,
  offset,
  crossOffset,
  showArrow = true,
  arrowClassName,
  onClose,
  ...props
}: TooltipProps) {
  const arrowOffset = showArrow ? 12 : 4;
  const finalOffset = offset ?? arrowOffset;

  return (
    <RACTooltip
      offset={finalOffset}
      {...(crossOffset !== undefined && { crossOffset })}
      {...props}
      className={composeRenderProps(
        className,
        (innerClassName, renderProps) =>
          cn(tooltipStyles(renderProps), onClose && "pointer-events-auto", innerClassName)!
      )}
    >
      {showArrow && (
        <OverlayArrow className="group">
          <svg
            width={12}
            height={12}
            viewBox="0 0 12 12"
            className={cn(
              "fill-primary stroke-primary/10 block stroke-1",
              "group-data-[placement=bottom]:rotate-180 group-data-[placement=left]:-rotate-90 group-data-[placement=right]:rotate-90",
              arrowClassName
            )}
          >
            <path d="M0 0 L6 6 L12 0" />
          </svg>
        </OverlayArrow>
      )}
      {children}
      {onClose && (
        <X
          className="size-4 min-w-4 cursor-pointer text-gray-300"
          onClick={e => {
            e.stopPropagation();
            onClose(e);
          }}
        />
      )}
    </RACTooltip>
  );
}
